import type { FC } from 'react'
import styles from './LogosSlider.module.scss'

export interface LogosSliderProps {
	sliceType?: string
	sliceVariation?: string
	color?: string
	logoColor?: string
	speed?: number
	heightDesktop?: number
	heightMobile?: number
	logos: Array<{ url: string; alt: string }>
}

// eslint-disable-next-line react/function-component-definition
const LogosSlider: FC<LogosSliderProps> = ({
	sliceType,
	sliceVariation,
	color = '#000000',
	logoColor,
	speed = 2,
	heightDesktop = 100,
	heightMobile = 60,
	logos
}) => {
	if (!logos.length) return null

	// Duplicate logos for seamless infinite scroll.
	// The marquee animation moves to -50%, so the track must contain an even number
	// of logo sets — the second half is identical to the first, creating a seamless loop.
	const minSets = Math.ceil(15 / logos.length)
	const sets = minSets % 2 === 0 ? minSets : minSets + 1
	const repeatedLogos = Array(sets).fill(logos).flat()

	const duration = Math.max(120 / speed, 10)

	const styleOverrides: React.CSSProperties & { [key: string]: string | number } = {}
	if (heightMobile !== 60) {
		styleOverrides.height = `${heightMobile}px`
		styleOverrides['--sdt-slider-mobile-height'] = `${heightMobile}px`
		styleOverrides['--sdt-slider-font-size'] = `${heightMobile * 0.5}px`
	}
	if (heightDesktop !== 100) {
		styleOverrides['--sdt-slider-desktop-height'] = `${heightDesktop}px`
		styleOverrides['--sdt-slider-desktop-font-size'] = `${heightDesktop * 0.5}px`
	}
	styleOverrides['--sdt-slider-duration'] = `${duration}s`

	return (
		<section
			data-slice-type={sliceType}
			data-slice-variation={sliceVariation}
			className="md:mx-5 flex-shrink-0"
			style={{ overflow: 'hidden' }}
		>
			<div style={{ width: '100vw', background: color, overflow: 'hidden', position: 'relative' }}>
				<div className={styles['sdt-slider']} style={styleOverrides}>
					{repeatedLogos.map((logo, i) => (
						<span
							// eslint-disable-next-line react/no-array-index-key
							key={`${logo.url}-${i}`}
							className={styles.logoItem}
						>
							<span
								role="img"
								aria-label={logo.alt || 'Brand logo'}
								className={styles.maskedLogo}
								style={{
									backgroundColor: logoColor || '#ffffff',
									maskImage: `url(${logo.url})`,
									WebkitMaskImage: `url(${logo.url})`
								}}
							>
								<img
									src={logo.url}
									alt={logo.alt || 'Brand logo'}
									className={styles.logoSizer}
								/>
							</span>
						</span>
					))}
				</div>
			</div>
		</section>
	)
}

export default LogosSlider
